import { Link } from "react-router-dom";
import { ArrowUpRight, Mail, ShoppingBag, Users } from "lucide-react";
import Seo from "../components/Seo";
import { Reveal } from "../components/Reveal";
import {
  BASE_KEYWORDS,
  CONTACT_EMAIL,
  PAGE_KEYWORDS,
  TACOVERSE_ORDER_URL,
  TACOVERSE_URL,
  breadcrumbSchema,
} from "../data/brand";

const CHANNELS = [
  {
    icon: Users,
    title: "Group events",
    copy: "Game days, watch parties, birthdays and crew gatherings — send the team an inquiry.",
    to: "/groups",
    cta: "Plan a group event",
    testid: "contact-groups",
  },
  {
    icon: Mail,
    title: "General questions",
    copy: "Questions, feedback or press — email the Barverse team directly.",
    href: `mailto:${CONTACT_EMAIL}`,
    cta: CONTACT_EMAIL,
    testid: "contact-email",
  },
  {
    icon: ShoppingBag,
    title: "Order online",
    copy: "Order Tacoverse favorites ahead on the Tacoverse ordering site.",
    href: TACOVERSE_ORDER_URL,
    cta: "Order online",
    testid: "contact-order",
  },
  {
    icon: ArrowUpRight,
    title: "Tacoverse food",
    copy: "The full restaurant experience lives at Tacoverse.",
    href: TACOVERSE_URL,
    cta: "Visit tacoverse.com",
    testid: "contact-tacoverse",
  },
];

const FAQS = [
  {
    q: "What is Barverse?",
    a: "Barverse is a modern bar and social experience from the Tacoverse family — a full cocktail and drinks program inside select Tacoverse locations, with the game on two big screens.",
  },
  {
    q: "Does Barverse serve food?",
    a: "Yes — the full Tacoverse food menu is served right inside Barverse. Menus and online ordering live at tacoverse.com.",
  },
  {
    q: "Can I watch sports at Barverse?",
    a: "Yes — two big screens keep you connected to the action. Programming varies by location and not every game is guaranteed, so check with your local Barverse.",
  },
  {
    q: "How do I plan a group event?",
    a: "Send a group inquiry — game days, watch parties, birthdays, after work, team gatherings and celebrations. It's a request, not an instant booking: our team follows up personally.",
  },
  {
    q: "Where is Barverse opening?",
    a: "Barverse is opening soon in the DFW area and North Florida, inside Tacoverse. Addresses, phones and bar hours are announced at opening.",
  },
  {
    q: "Does Barverse take reservations?",
    a: "There's no reservation system — group events start with an inquiry, and our team confirms the details with you directly.",
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export default function Contact() {
  return (
    <div className="pt-28 md:pt-36" data-testid="contact-page">
      <Seo
        title="Contact — Group Events & General Questions"
        description="Contact the Barverse team — group events, general questions, online ordering and the Tacoverse food connection. Answers to common Barverse questions."
        path="/contact"
        jsonLd={[breadcrumbSchema("/contact", "Contact"), faqSchema]}
        keywords={`${BASE_KEYWORDS}, Tacoverse contact, group inquiry, bar FAQ`}
      />
      <header className="container-x">
        <Reveal>
          <p className="eyebrow">Contact</p>
          <h1 className="mt-5 font-display text-6xl uppercase leading-[0.9] text-bv-ink sm:text-7xl lg:text-8xl">
            Talk to us.
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-bv-sub">
            Questions, feedback or press? Reach the Barverse team through one of these — and check
            the <Link to="/locations" className="text-bv-flame transition-colors hover:text-bv-ink">locations page</Link> for the latest openings.
          </p>
        </Reveal>
      </header>

      <section className="container-x mt-14 grid gap-6 pb-16 md:grid-cols-2 lg:grid-cols-4">
        {CHANNELS.map((c, i) => {
          const Icon = c.icon;
          const body = (
            <>
              <Icon className="h-6 w-6 text-bv-flame" aria-hidden="true" />
              <h2 className="mt-5 font-display text-2xl uppercase tracking-wide text-bv-ink">{c.title}</h2>
              <p className="mt-3 flex-1 text-sm leading-relaxed text-bv-sub">{c.copy}</p>
              <span className="mt-6 font-mono text-[11px] font-bold uppercase tracking-[0.18em] text-bv-flame transition-colors group-hover:text-bv-ink">
                {c.cta}
              </span>
            </>
          );
          const cls =
            "flex h-full flex-col rounded-2xl border border-bv-line bg-bv-panel p-8 transition-colors duration-200 hover:border-bv-flame/60";
          return (
            <Reveal key={c.title} delay={i * 0.06}>
              {c.to ? (
                <Link to={c.to} className={cls} data-testid={c.testid}>{body}</Link>
              ) : (
                <a href={c.href} className={cls} data-testid={c.testid}>{body}</a>
              )}
            </Reveal>
          );
        })}
      </section>

      <section className="container-x pb-24" aria-label="Common questions" data-testid="faq">
        <Reveal>
          <h2 className="font-display text-3xl uppercase text-bv-ink md:text-4xl">Common questions</h2>
        </Reveal>
        <div className="mt-8 grid gap-x-10 gap-y-8 md:grid-cols-2">
          {FAQS.map((f, i) => (
            <Reveal key={f.q} delay={i * 0.04}>
              <div className="border-b border-bv-line pb-6">
                <h3 className="font-display text-lg uppercase text-bv-ink">{f.q}</h3>
                <p className="mt-2 text-sm leading-relaxed text-bv-sub">{f.a}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}
